import { differenceInCalendarDays, isSameDay } from 'date-fns'
import { scheduler_response } from '@builder/mock-api/scheduler-response'

export default function modifyScheduleData(
  planningResponse,
  defaultCoursePlan,
  startDate: string | Date = '2025-03-25T18:30:00'
) {
  if (Object.keys(planningResponse).length === 0) {
    return null
  }
  const sessionScheduleOrder = [
    'locked',
    'standard',
    'weekend',
    'postweekend',
    'available'
  ]
  const scheduleSessions = {
    training: [],
    course: [],
    totalWeekendSessions: 0
  }
  let episodeCounter = 1

  scheduleSessions.totalWeekendSessions = (
    defaultCoursePlan.weekend || []
  ).length

  const trainingEpisodeNumberMap = defaultCoursePlan?.training.reduce(
    (accumulator, { id }, index) => {
      accumulator[id] = index + 1 // Assuming episode numbers start from 1
      return accumulator
    },
    {}
  )

  //course episode numbers
  const courseEpisodeNumberMap = sessionScheduleOrder.reduce(
    (acc, category) => {
      ;(defaultCoursePlan[category] || []).forEach(({ id }) => {
        acc[id] = episodeCounter++
      })
      return acc
    },
    {}
  )

  const weekendSessionNumberMap = defaultCoursePlan.weekend.reduce(
    (accumulator, { id }, index) => {
      accumulator[id] = index + 1
      return accumulator
    },
    {}
  )
  // Combine all items in different buckets into a single array
  const alphaSessions = Object.entries(planningResponse).flatMap(
    ([key, items]) => {
      return (items as Array<any>).map((item) => ({
        date: item.date,
        id: item.id,
        episodeName: item.title,
        category: key,
        ...(item.skipped === true ? { skipped: true } : {})
      }))
    }
  )

  const updatedItems = findSessionStatus(alphaSessions)

  const sessionStatusMap = updatedItems.reduce((acc, { id, status }) => {
    acc[id] = status // Apply setStatus to each item's date
    return acc
  }, {})

  const courseSessions = sessionScheduleOrder.reduce((acc, category) => {
    ;(planningResponse[category] || []).forEach(
      ({ id, title, thumbnail, date, type, skipped }) => {
        let inAvailableBucket = false
        if (category !== 'locked') {
          inAvailableBucket = !!planningResponse?.available?.find(
            (item) => item.id === id
          )
        }

        acc.push({
          id,
          title,
          thumbnail,
          date,
          type,
          episodeNumber: courseEpisodeNumberMap[id],
          weekendEpisodeNumber: weekendSessionNumberMap[id] ?? null,
          skipped: !!inAvailableBucket || !!skipped,
          status: sessionStatusMap[id],
          numDaysFromStart: numberOfDaysSinceStart(date, startDate),
          category: category
        })
      }
    )
    return acc
  }, [])

  const trainingSessions = planningResponse.training.map(
    ({ id, title, thumbnail, date, type, skipped }) => ({
      id,
      title,
      thumbnail,
      date,
      type,
      trainingEpisodeNumber: trainingEpisodeNumberMap[id],
      skipped: skipped ?? false, //!!planningResponse?.available?.find((item) => item.id === id),
      numDaysFromStart: numberOfDaysSinceStart(date, startDate),
      status: sessionStatusMap[id],
      category: 'training'
    })
  )

  scheduleSessions.training = trainingSessions

  scheduleSessions.course = courseSessions

  return scheduleSessions
}

export function setStatus(
  sessionDate: string | Date,
  hasCurrentSession: boolean = false
): { status: 'completed' | 'current' | 'upcoming'; updatedFlag: boolean } {
  if (numberOfDaysSinceStart(sessionDate, new Date()) < 0) {
    return { status: 'completed', updatedFlag: hasCurrentSession }
  } else if (
    numberOfDaysSinceStart(sessionDate, new Date()) >= 0 &&
    !hasCurrentSession
  ) {
    return { status: 'current', updatedFlag: true }
  }
  return { status: 'upcoming', updatedFlag: hasCurrentSession }
}

export function numberOfDaysSinceStart(sessionDate, startDate) {
  return differenceInCalendarDays(
    sessionDate instanceof Date ? sessionDate : new Date(sessionDate),
    startDate instanceof Date ? startDate : new Date(startDate)
  )
}

export type Session = {
  category: string
  date: string
  trainingNumber?: number
  episodeNumber?: number
  skipped?: boolean
  id: number
}

export type Sessions = {
  training: Session[]
  course: Session[]
  totalWeekendSessions: number
}

export function handleSkipSession(
  skippedItemId: number,
  category: string,
  alpha,
  defaultCoursePlan,
  saveSchedule,
  setAllSessions
) {
  // Find the item's original category from defaultCoursePlan
  let sourceCategory: string | null = null
  let originalItem = null

  for (const [key, items] of Object.entries(defaultCoursePlan)) {
    const match = (items as Array<{ id: number }>).find(
      (item) => item.id === skippedItemId
    )
    if (match) {
      sourceCategory = key
      originalItem = { ...match }
      break
    }
  }

  if (!originalItem || !sourceCategory) return

  if (category === 'available') {
    // Move from available to original category
    const indexInAvailable = alpha.planning.available.findIndex(
      (item) => item.id === skippedItemId
    )
    if (indexInAvailable !== -1) {
      const [movedItem] = alpha.planning.available.splice(indexInAvailable, 1)
      movedItem.skipped = false
      alpha.planning[sourceCategory].push(movedItem)
    }
  } else {
    const categoryList = alpha.planning[category]
    const indexInCategory = categoryList.findIndex(
      (item) => item.id === skippedItemId
    )
    // Move from specific category to available only if it's not locked or training
    if (category === 'locked' || category === 'training') {
      alpha.planning[sourceCategory][indexInCategory].skipped =
        !alpha.planning[sourceCategory][indexInCategory].skipped
    } else {
      if (indexInCategory !== -1) {
        const [movedItem] = categoryList.splice(indexInCategory, 1)
        movedItem.skipped = true
        alpha.planning.available.push(movedItem)
      }
    }
  }

  saveSchedule()
  setAllSessions(
    modifyScheduleData(alpha.planning, defaultCoursePlan, alpha.date)
  )
}

export function sortSessions(sessionList: Session[]) {
  return [...sessionList].sort((a, b) => {
    const dateA = new Date(a.date)
    const dateB = new Date(b.date)

    dateA.setHours(0, 0, 0, 0)
    dateB.setHours(0, 0, 0, 0)

    const dateComparison = dateA.getTime() - dateB.getTime()

    if (dateComparison === 0) {
      const isATraining = a.category === 'training' ? 0 : 1
      const isBTraining = b.category === 'training' ? 0 : 1

      if (isATraining !== isBTraining) {
        return isATraining - isBTraining
      }

      const numA = a.trainingNumber ?? a.episodeNumber ?? 0
      const numB = b.trainingNumber ?? b.episodeNumber ?? 0
      return numA - numB
    }

    return dateComparison
  })
}

export function handleSaveSessionDate(
  id,
  newDate,
  category: string,
  alpha,
  defaultCoursePlan,
  saveSchedule,
  setAllSessions
) {
  const collection = alpha.planning[category]
  collection.forEach((item, index) => {
    if (item.id === id) {
      alpha.planning[category][index].date = newDate
      setAllSessions(
        alpha.date
          ? modifyScheduleData(alpha.planning, defaultCoursePlan, alpha.date)
          : null
      )
    }
  })
  saveSchedule()
}

export function findSessionStatus(alphaSessions: any[]) {
  // Assign statuses (completed, current, upcoming)
  let hasCurrent = false
  const sortedSessions = sortSessions(alphaSessions)
  const updatedItems = sortedSessions.map((item) => {
    const sessionDate = new Date(item.date)
    const sessionSkipped = item.skipped
    const { status, updatedFlag } = setStatus(sessionDate, hasCurrent)
    if (!sessionSkipped) {
      hasCurrent = updatedFlag
    }
    return {
      ...item,
      status // Attach the calculated status
    }
  })
  return updatedItems
}

/*
  For use when "schedule-move-it" is OFF
  For supplementing the planning API request payload with the new Scheduler data format
*/
export function legacyCompatibilityAdapter(UIState) {
  return {}
}

export function reverseCompatibilityAdapter(obj) {
  const schedule = {
    training: [],
    course: [],
    totalWeekendSessions: 0
  }

  const legacySchedule = {
    training: [],
    standard: [],
    weekend: [],
    postweekend: [],
    locked: [] // This is untouched in the original, still returned empty
  }

  // Reconstruct training
  obj.training.forEach((session) => {
    const { id, date, ...rest } = session
    schedule.training.push({ id, date })
    legacySchedule.training.push(rest)
  })

  // Reconstruct course from standard, weekend, postweekend, available
  const allCourseSessions = [
    ...obj.standard.map((s) => ({ ...s, category: 'standard' })),
    ...obj.weekend.map((s) => ({
      ...s,
      category: 'weekend'
    })),
    ...obj.postweekend.map((s) => ({ ...s, category: 'postweekend' })),
    ...obj.available.map((s) => ({ ...s, skipped: true }))
  ]

  allCourseSessions.forEach((session) => {
    const { id, date, category, weekendEpisodeNumber, skipped, ...rest } =
      session

    schedule.course.push({
      id,
      category,
      date,
      ...(weekendEpisodeNumber !== undefined ? { weekendEpisodeNumber } : {}),
      ...(skipped ? { skipped: true } : {})
    })

    if (!legacySchedule[category]) {
      legacySchedule[category] = []
    }

    legacySchedule[category].push({
      id,
      ...rest // the original session properties except date
    })
  })

  return { schedule, ...legacySchedule }
}

/*
  For use when "schedule-move-it" is ON
  For supplementing the planning API request payload with the legacy Scheduler data format
*/
// export function compatibilityAdapter(schedule, legacySchedule) {
//   const obj = {
//     training: [],
//     standard: [],
//     weekend: [],
//     postweekend: [],
//     locked: [],
//     available: [],
//     schedule
//   }
//
//   schedule?.training.forEach(({ id, date }) => {
//     const updatedSession = legacySchedule.training.find(
//       (item) => item.id === id
//     )
//     updatedSession.date = date
//     obj.training.push(updatedSession)
//   })
//
//   let firstWeekendEpisodeDate = null
//   schedule?.course.forEach(
//     ({ id, category, date, weekendEpisodeNumber, skipped }) => {
//       const updatedSession = legacySchedule[category].find(
//         (item) => item.id === id
//       )
//       updatedSession.date = date
//       delete updatedSession.skipped
//
//       if (weekendEpisodeNumber === 1) {
//         firstWeekendEpisodeDate = new Date(date)
//       }
//       if (skipped) {
//         updatedSession.skipped = true
//         obj.available.push(updatedSession)
//       } else if (!firstWeekendEpisodeDate) {
//         obj.standard.push(updatedSession)
//         // May need date fn comparator method to compare only the month/day/year
//       } else if (isSameDay(new Date(firstWeekendEpisodeDate), new Date(date))) {
//         obj.weekend.push(updatedSession)
//       } else {
//         obj.postweekend.push(updatedSession)
//       }
//     }
//   )
//   return obj
// }
export function compatibilityAdapter(schedule, legacySchedule) {
  const result = {
    training: [],
    standard: [],
    weekend: [],
    postweekend: [],
    locked: [],
    available: [],
    schedule
  }

  // Handle training
  schedule?.training?.forEach(({ id, date }) => {
    const original = legacySchedule.training.find((item) => item.id === id)
    if (!original) return

    const session = { ...original, date }
    result.training.push(session)
  })

  // Handle course
  let firstWeekendDate = null

  schedule?.course?.forEach(
    ({ id, category, date, weekendEpisodeNumber, skipped }) => {
      const original = legacySchedule[category]?.find((item) => item.id === id)
      if (!original) return

      const session = { ...original, date } // clone and update date

      // Skip flag
      if (skipped) {
        session.skipped = true
        result.available.push(session)
        return
      }

      // Identify first weekend episode date
      const sessionDate = new Date(date)
      if (weekendEpisodeNumber === 1) {
        firstWeekendDate = sessionDate
      }

      if (!firstWeekendDate) {
        session.category = 'standard'
        result.standard.push(session)
      } else if (isSameDay(firstWeekendDate, sessionDate)) {
        result.weekend.push(session)
      } else {
        result.postweekend.push(session)
      }
    }
  )

  return result
}

export function save(alpha, updatedState, saveSchedule, isMoveitEnabled) {
  if (isMoveitEnabled) {
    alpha.planning = compatibilityAdapter(
      scheduler_response.schedule,
      scheduler_response
    )
  } else {
    // alpha.planning = syncNewSchedule(alpha.planning)
  }
  saveSchedule()
}
